import {
  enrollmentCodeStatusResponse,
  resolveEnrollmentCodeResponse,
  type EnrollmentCodeStatusResponse,
  type ResolveEnrollmentCodeResponse,
} from '@occ/contracts';
import { HttpError, request } from '../api/http';
import { ROUTES } from '../api/routes';

/**
 * Las dos llamadas de la página pública de instalación (`/i`, §6 y §7).
 *
 * **Sin sesión, y por eso van con `request({auth: false})` y no con el helper
 * `post`.** Quien abre esta página es la caja del cliente, que no tiene sesión
 * del panel ni debe tenerla: mandar una cabecera `Authorization` con lo que
 * hubiera en el almacén local sería, en el mejor caso, un token del técnico
 * viajando a un endpoint que no lo mira, y en el peor, una renovación de sesión
 * disparada desde una máquina ajena.
 *
 * Se valida con los esquemas de `@occ/contracts` y no con parsers tolerantes: la
 * forma la fijó el backend en F4 y es la misma que consume el catálogo de
 * acciones, así que una discrepancia tiene que verse aquí y no como un comando a
 * medias en la caja del cliente.
 */

export const resolveInstallCode = (code: string): Promise<ResolveEnrollmentCodeResponse> =>
  request({
    method: 'POST',
    path: ROUTES.enrollmentCodeResolve,
    body: { code },
    schema: resolveEnrollmentCodeResponse,
    auth: false,
  });

export const fetchInstallCodeStatus = (code: string): Promise<EnrollmentCodeStatusResponse> =>
  request({
    method: 'POST',
    path: ROUTES.enrollmentCodeStatus,
    body: { code },
    schema: enrollmentCodeStatusResponse,
    auth: false,
  });

/**
 * Por qué no salió el comando. Son dos cosas distintas y llevan a acciones
 * opuestas, así que la página no las pinta igual.
 *
 * - `code`: el código no sirve. El backend ya redacta el motivo dentro del `404`
 *   —caducado, revocado, agotado o inexistente (§13.8)— y aquí no se reinterpreta:
 *   traducirlo sería tener dos versiones del mismo texto y enterarse cuando se
 *   separen. Quien lo lee tiene que volver a teclear o pedir otro código.
 * - `service`: el código puede estar perfecto. Un `429` es el corte global del
 *   §10, un `503` es Redis caído y un `0` es que la caja no llega al OCC. En los
 *   tres, la salida es la misma y no es teclear otra vez: el token largo desde el
 *   panel sigue funcionando entero, que es exactamente el intercambio que el §10
 *   elige.
 */
export type InstallCodeFailure = {
  kind: 'code' | 'service';
  message: string;
};

export const describeInstallCodeFailure = (error: unknown): InstallCodeFailure => {
  if (!(error instanceof HttpError)) {
    return {
      kind: 'service',
      message:
        error instanceof Error
          ? error.message
          : 'No se pudo consultar el código. Vuelve a intentarlo.',
    };
  }

  // 404 es el rechazo del código con su motivo dentro, y 400 es un código que ni
  // siquiera tiene forma de código. Los dos se arreglan en el campo de arriba.
  if (error.status === 404 || error.status === 400) {
    return { kind: 'code', message: error.message };
  }

  if (error.status === 0) {
    return {
      kind: 'service',
      message:
        'Esta máquina no llega al OCC. Comprueba que tiene salida a internet — el instalador ' +
        'también la va a necesitar para descargar el agente.',
    };
  }

  // El resto llega con el mensaje ya escrito por el backend (el `429` del corte
  // global y el `503` de Redis caído dicen los dos que se pida el comando en el
  // panel), y ese texto es mejor que cualquier cosa que se pueda inventar aquí.
  return { kind: 'service', message: error.message };
};
